import { ICommonSettings, IDBConnectionStringSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const DB_CONNECTION_STRING_SETTINGS_SEARCH_TERMS: string[] = ["Connection String"];

export interface IConfigDBConnectionStringSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingDBConnectionString?: IDBConnectionStringSetting;
}

export interface IConfigDBConnectionStringSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingDBConnectionStringId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
  isWorkflow?: boolean;
}

export class ConfigDBConnectionStringSettings extends React.PureComponent<
  IConfigDBConnectionStringSettingsProps,
  IConfigDBConnectionStringSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigDBConnectionStringSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingDBConnectionString: undefined
    };
  }

  componentDidMount() {
    this.loadDBConnectionStringSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigDBConnectionStringSettingsProps & IWithSDProps) {
    if (prevProps.editingDBConnectionStringId && !this.props.editingDBConnectionStringId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingDBConnectionStringId !== this.props.editingDBConnectionStringId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadDBConnectionStringSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadDBConnectionStringSettings();
    }
  }

  loadDBConnectionStringSettings = () => {
    if (!this.props.editingDBConnectionStringId) return;

    const dbConnectionStringSetting = _.cloneDeep(this.props.commonSettings?.dbConnectionStringSettings);
    const editingDBConnectionString = dbConnectionStringSetting?.find(
      (setting) => setting.id === this.props.editingDBConnectionStringId
    );
    if (editingDBConnectionString)
      this.setState({ editingDBConnectionString }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingDBConnectionString);
      });
  };

  existingNameValidator = async (_rule, value) => {
    const dbConnectionStringSettings = this.props.commonSettings?.dbConnectionStringSettings;

    const isSettingAlreadyExist =
      dbConnectionStringSettings?.some((x) => x.name === value && x.id !== this.props.editingDBConnectionStringId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("DB Connection String Setting Name is already exist.");
  };

  onFinish = async (data: any) => {
    this.saveStatus.start();
    const obj: IDBConnectionStringSetting = {
      id:
        this.props.editingDBConnectionStringId && this.props.editingDBConnectionStringId !== ""
          ? this.props.editingDBConnectionStringId
          : Guid.create().toString(),
      name: data.name,
      dbConnectionString: data.dbConnectionString?.trim()
    };
    if (this.props.editingDBConnectionStringId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "dbConnectionStringSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "dbConnectionStringSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteDBConnectionString = () => {
    if (!this.props.editingDBConnectionStringId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingDBConnectionStringId || "",
      configKey: "dbConnectionStringSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`DB Connection String setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader={`${this.props.isWorkflow ? "Workflow " : ""}DB ConnectionString setting`}
        secondHeader={`${this.props.isWorkflow ? "Workflow " : ""}DB Connection String setting`}
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingDBConnectionStringId ? this.deleteDBConnectionString : undefined}
            deleteTestId="dbConnectionStringSettingDeleteButton"
            submitTestId="dbConnectionStringSettingCreateButton"
            submitLabel={this.props.editingDBConnectionStringId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configDbConnectionStringForm"
          />
        }
      >
        <Form
          id="configDbConnectionStringForm"
          name="dbConnectionStringSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              this.props.isWorkflow
                ? "appsettings_dbconnectionstringsettingpop_up_description"
                : "dbconnectionsettings_dbconnectionsettings_description"
            )}
          >
            <div>
              <ConfigField
                label="Name"
                required
                tooltipContentKey={
                  this.props.isWorkflow
                    ? "appsettings_dbconnectionstringsettingpop_up_name"
                    : "dbconnectionsettings_dbconnectionsettings_name"
                }
              >
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a DB Connection String Setting Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "DB Connection String Setting Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="dbConnectionStringSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Connection String"
                required
                tooltipContentKey={
                  this.props.isWorkflow
                    ? "appsettings_dbconnectionstringsettingpop_up_connectionstring"
                    : "dbconnectionsettings_dbconnectionsettings_connectionstring"
                }
              >
                <Form.Item
                  name="dbConnectionString"
                  rules={[
                    {
                      whitespace: true,
                      required: true,
                      message: "Please enter a DB Connection String."
                    }
                  ]}
                >
                  <KuikaInput size="md" data-testid="dbConnectionStringSettingConnectionStringInput" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
